import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 180000; // give up after 3 minutes

/**
 * Polls a creative generation job and fires a sonner toast when it reaches a
 * terminal state (complete / failed). Designed to live at the page level so the
 * notification survives tab switches inside the Creative Generator.
 *
 * Polling stops automatically on completion, on unmount, or after a timeout.
 *
 * @param {string|null} jobId   The job to watch. Pass null/undefined to idle.
 * @param {object} options
 * @param {() => void} [options.onComplete]  Called once when the job completes.
 * @param {() => void} [options.onFailed]    Called once when the job fails.
 * @param {() => void} [options.onViewHistory] Toast action handler (e.g. switch to History tab).
 */
export function useCreativeJobPoll(jobId, { onComplete, onFailed, onViewHistory } = {}) {
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const settledRef = useRef(false);

  // Keep the latest callbacks without restarting the poll loop.
  const callbacksRef = useRef({ onComplete, onFailed, onViewHistory });
  useEffect(() => {
    callbacksRef.current = { onComplete, onFailed, onViewHistory };
  }, [onComplete, onFailed, onViewHistory]);

  useEffect(() => {
    if (!jobId) return undefined;

    settledRef.current = false;

    const stop = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      intervalRef.current = null;
      timeoutRef.current = null;
    };

    const poll = async () => {
      if (settledRef.current) return;
      try {
        const response = await axios.get(`${API}/creative/job/${jobId}`);
        const status = response.data?.status;

        if (status === 'complete') {
          settledRef.current = true;
          stop();
          toast.success('Creative ready', {
            description: 'Your generated creative is in History.',
            action: callbacksRef.current.onViewHistory
              ? { label: 'View', onClick: () => callbacksRef.current.onViewHistory() }
              : undefined,
          });
          callbacksRef.current.onComplete?.();
        } else if (status === 'failed') {
          settledRef.current = true;
          stop();
          toast.error('Creative generation failed', {
            description: response.data?.error_message || 'Something went wrong. Please try again.',
          });
          callbacksRef.current.onFailed?.();
        }
      } catch (error) {
        // Transient errors are tolerated; the timeout is the hard stop.
        console.error('Creative job poll error:', error);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    timeoutRef.current = setTimeout(() => {
      if (!settledRef.current) stop();
    }, POLL_TIMEOUT_MS);

    return stop;
  }, [jobId]);
}

export default useCreativeJobPoll;
